import { useQuery } from '@apollo/client';
import axios from 'axios';
import { graphql, useStaticQuery } from 'gatsby';
import React, { FC, useContext } from 'react';
import { useAsync, useDeepCompareEffect } from 'react-use';

import { IAuthenticatedMember, IMultiFactorConfiguration } from '@hooks/useMemberInfo';

import { keysToCamel } from '@utils/butter';
import { ApiEndpoints } from '@utils/endpoints';
import { combineMemberFragments } from '@utils/graphql';

import { useAuth } from '@providers/AuthProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IAccountInfoQueryData } from '@folx-types/member';

import {
	multiFactor,
	multiFactorName,
	queryName,
} from '@graphql/fragments/multiFactorConfiguration';
import ACCOUNT_INFO_QUERY from '@graphql/queries/AccountInfo.query.gql';

import AccountInfoEditStep, { IAccountEditInfoContent } from './AccountInfoEditStep.component';

interface IAccountInfoEditStepPageProps {
	path: string;
}

const AccountInfoEditStepPage: FC<IAccountInfoEditStepPageProps> = ({ path, ...props }) => {
	const { getAuthToken } = useAuth();

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const { loading: multiFactorLoading, data: multiFactorData } = useQuery<
		IAuthenticatedMember<IMultiFactorConfiguration>
	>(combineMemberFragments([multiFactorName], [multiFactor], queryName), {
		client: graphQLv2Client,
	});

	const { loading: accountLoading, data: accountData } =
		useQuery<IAccountInfoQueryData>(ACCOUNT_INFO_QUERY);

	const member = accountData?.currentMember;

	const {
		value: selfie,
		error: selfieError,
		loading: selfieLoading,
	} = useAsync(async () => {
		const authorizationToken = await getAuthToken();
		const selfieData = await axios.get(`${ApiEndpoints.memberPortal}/image/selfieImage`, {
			headers: {
				Accept: 'image/png, image/jpeg, image/gif, application/pdf',
				authorizationToken,
			},
			responseType: 'blob',
		});

		if (!selfieData) return;
		return URL.createObjectURL(selfieData.data);
	}, []);

	const { pageContent } = useStaticQuery<Record<string, string>>(
		graphql`
			{
				pageContent: butterPage(slug: { eq: "mssp-account-edit-page" }) {
					page_header
					profile_picture_label
					first_name_label
					first_name_placeholder
					last_name_label
					last_name_placeholder
					pronouns_label
					email_label
					email_disclaimer
					phone_number_label
					phone_number_disclaimer
					phone_number_placeholder
					innerspace_label
					innerspace_placeholder
					update_account_error_text
				}
			}
		`
	);

	useDeepCompareEffect(() => {
		if (!selfieError) return;
		console.log(selfieError);
	}, [selfieError]);

	const loading = accountLoading || selfieLoading || multiFactorLoading;

	const smsEnrolled = !!multiFactorData?.authenticatedMember?.multiFactorConfiguration?.smsEnrolled;

	const refinedContent: IAccountEditInfoContent = keysToCamel(
		pageContent
	) as IAccountEditInfoContent;

	return (
		<AccountInfoEditStep
			loading={loading}
			member={member}
			pageContent={refinedContent}
			selfie={selfie}
			selfieError={selfieError}
			smsEnrolled={smsEnrolled}
		/>
	);
};

export default AccountInfoEditStepPage;
